/**
 * Billing API Service
 * Handles all API calls related to billing — credit balance,
 * purchasing credit packs, Stripe checkout & portal, feature flags.
 */

import { apiCall as baseApiCall } from './client';

const USER_SERVICE_URL = import.meta.env.VITE_USER_SERVICE_URL || 'http://localhost:5001';

const getApiKey = (): string | null => {
  return localStorage.getItem('crawl_ai_api_key');
};

async function apiCall<T>(
  url: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<T> {
  const apiKey = getApiKey();
  return baseApiCall<T>(url, {
    ...options,
    headers: {
      ...(apiKey && { 'X-API-Key': apiKey }),
      ...options.headers,
    },
  }, signal);
}

// ============================================================================
// Types
// ============================================================================

export interface BillingSubscription {
  id: number;
  customer_id: number;
  product_id: number;
  tier_level: number;
  status: string;
  billing_cycle: string;
  current_period_start: string | null;
  current_period_end: string | null;
  feature_flags: Record<string, boolean>;
}

export interface BillingCredits {
  balance: number;
  reserved: number;
  available: number;
  monthly_spent: number;
  monthly_limit: number;
}

export interface BillingUsageItem {
  product_id: number | null;
  usage_type: string;
  total_quantity: number;
  total_credits: number;
}

export interface CreditPack {
  id: number;
  name: string;
  slug: string;
  credits: number;
  price_cents: number;
  currency: string;
  product_id: number | null;
  is_active: boolean;
  sort_order: number;
}

export interface CreditPrice {
  id: number;
  product_id: number | null;
  usage_type: string;
  credits_per_unit: number;
  unit_label: string;
  description: string | null;
  is_active: boolean;
}

export interface BillingInvoice {
  id: number;
  stripe_invoice_id: string | null;
  amount_cents: number;
  currency: string;
  status: string;
  description: string | null;
  created_at: string;
}

export interface DeploymentInfo {
  id: number;
  status: string;
  full_domain: string | null;
}

export interface TrialInfo {
  is_trial: boolean;
  trial_ends_at: string | null;
  days_remaining: number | null;
  setup_bonus_active: boolean;
}

export interface BillingOverview {
  subscriptions: BillingSubscription[];
  credits: BillingCredits;
  usage: BillingUsageItem[];
  credit_packs: CreditPack[];
  credit_prices: CreditPrice[];
  invoices: BillingInvoice[];
  deployment: DeploymentInfo | null;
  trial_info?: TrialInfo;
}

export interface CreditBalance {
  balance: number;
  reserved: number;
  available: number;
  monthly_spent: number;
  monthly_limit: number;
}

export interface FeatureFlags {
  tier_level: number;
  features: Record<string, boolean>;
  subscription_status: string;
  credits_remaining: number;
  current_period_ends: string | null;
}

export interface CheckoutSession {
  session_id: string;
  url: string;
}

export interface PortalSession {
  url: string;
}

export interface DeploymentFeatures {
  deployment_type: 'saas' | 'enterprise' | 'manual';
  credits_enabled: boolean;
  /** Core competitor/domain scraping capability. False for Platform-Only plans. */
  scraping_enabled: boolean;
  /** Platform comparison (Amazon, eBay, etc.) feature access. */
  platforms_enabled: boolean;
  /** true = platform is an add-on to a scraping plan (Standard tier).
   *  false = platform is a standalone product (Platform-Only tier). */
  platform_addon_booked: boolean;
  /** Domain monitoring feature visibility. Derived from scraping_enabled when SaaS. */
  domains_enabled: boolean;
  tier_level?: number | null;
  features?: {
    settings_proxy_visible?: boolean;
    settings_api_visible?: boolean;
    billing_visible?: boolean;
    max_concurrent_crawls?: number;
    max_scheduled_jobs?: number;
  };
}

export interface PlanCatalogItem {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  base_price_cents: number;
  annual_price_cents: number | null;
  included_credits: number;
  platform_credits: number | null;
  platform_addon_price_cents: number | null;
  trial_credits: number | null;
  setup_bonus_pct: number | null;
  overage_rate_eur_per_credit: number | null;
  feature_flags: Record<string, boolean | number | null> | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_annual: string | null;
  stripe_addon_price_id: string | null;
  is_active: boolean;
  sort_order: number;
  synced_at: string | null;
}

export interface LocalCreditBalance {
  subscription_balance: number;
  purchased_balance: number;
  total: number;
  reserved: number;
  available: number;
  subscription_expires_at: string | null;
}

// ============================================================================
// API Methods
// ============================================================================

export const billingAPI = {
  getOverview: async (): Promise<BillingOverview> => {
    return apiCall<BillingOverview>(`${USER_SERVICE_URL}/api/billing/overview`);
  },

  getBalance: async (): Promise<CreditBalance> => {
    return apiCall<CreditBalance>(`${USER_SERVICE_URL}/api/billing/balance`);
  },

  getFeatures: async (productId?: number): Promise<FeatureFlags> => {
    const params = productId ? `?product_id=${productId}` : '';
    return apiCall<FeatureFlags>(`${USER_SERVICE_URL}/api/billing/features${params}`);
  },

  getPacks: async (productId?: number): Promise<CreditPack[]> => {
    const params = productId ? `?product_id=${productId}` : '';
    return apiCall<CreditPack[]>(`${USER_SERVICE_URL}/api/billing/packs${params}`);
  },

  getPrices: async (productId?: number): Promise<CreditPrice[]> => {
    const params = productId ? `?product_id=${productId}` : '';
    return apiCall<CreditPrice[]>(`${USER_SERVICE_URL}/api/billing/prices${params}`);
  },

  createCheckout: async (
    creditPackId: number,
    successUrl: string,
    cancelUrl: string,
    productId?: number
  ): Promise<CheckoutSession> => {
    return apiCall<CheckoutSession>(`${USER_SERVICE_URL}/api/billing/checkout`, {
      method: 'POST',
      body: JSON.stringify({
        credit_pack_id: creditPackId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        product_id: productId,
      }),
    });
  },

  getPortalUrl: async (returnUrl: string): Promise<PortalSession> => {
    return apiCall<PortalSession>(`${USER_SERVICE_URL}/api/billing/portal`, {
      method: 'POST',
      body: JSON.stringify({ return_url: returnUrl }),
    });
  },

  getDeploymentFeatures: async (): Promise<DeploymentFeatures> => {
    return apiCall<DeploymentFeatures>(`${USER_SERVICE_URL}/api/billing/features`);
  },

  getLocalCreditBalance: async (): Promise<LocalCreditBalance> => {
    return apiCall<LocalCreditBalance>(`${USER_SERVICE_URL}/api/billing/balance`);
  },

  getPlans: async (): Promise<PlanCatalogItem[]> => {
    return apiCall<PlanCatalogItem[]>(`${USER_SERVICE_URL}/api/billing/plans`);
  },

  checkCredits: async (
    action: string,
    quantity: number
  ): Promise<{ sufficient: boolean; available: number; required: number }> => {
    return apiCall<{ sufficient: boolean; available: number; required: number }>(
      `${USER_SERVICE_URL}/api/billing/credits/check`,
      {
        method: 'POST',
        body: JSON.stringify({ action, quantity }),
      }
    );
  },
};
